/**
 * Vellatini PDP — in-place sibling swap (colour dots + size rungs).
 *
 * Renditions and sizes are SIBLING PRODUCTS (blocks/vellatini-material-dots.liquid,
 * blocks/vellatini-size-ladder.liquid render plain <a href> to them). Pixel data
 * 2026-09-08→10: 895 dot + 412 rung clicks per ~2,250 sessions, every one a full
 * navigation — PDP views doubled, scroll position and buy box lost on each hop,
 * shoppers hopping 3–7 renditions before deciding.
 *
 * Same technique Horizon uses for combined-listing products (assets/variant-picker.js
 * `updateMain`) and Afroyla uses for its size siblings: fetch the sibling page, morph
 * `<main>` with @theme/morph (custom elements reconnect, focus and scroll survive),
 * push the sibling URL, re-fire the product view event. The anchors stay real links
 * for no-JS, crawlers and modifier-key clicks.
 *
 * Contract for other modules: after a swap `document` receives
 * `vellatini:pdp:swapped` {detail:{url}} — vellatini-pdp-sticky-buybox.js re-measures,
 * vellatini-pair-picker.js re-boots on the new root (its root carries
 * data-vt-swap-key="{{ product.id }}" so morph replaces it instead of patching it).
 */
import { morph, MORPH_OPTIONS } from '@theme/morph';

const SELECTOR = 'a.pdp-dots__opt, a.pdp-ladder__rung';
const MEDIA_SELECTOR = '.product-information__media';
const CACHE_MAX = 8;
/** @type {Map<string, Promise<Document | null>>} */
const cache = new Map();
let swapping = false;

/** @param {Element | null} a */
function isSiblingLink(a) {
  return (
    a instanceof HTMLAnchorElement &&
    a.matches(SELECTOR) &&
    !a.hasAttribute('aria-current') &&
    a.origin === location.origin &&
    a.pathname.startsWith('/products/')
  );
}

/** @param {HTMLAnchorElement} a */
const urlOf = (a) => a.pathname + a.search;

/** @param {string} url */
function fetchDoc(url) {
  const hit = cache.get(url);
  if (hit) return hit;
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
  const p = fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((t) => new DOMParser().parseFromString(t, 'text/html'))
    .catch(() => {
      cache.delete(url);
      return null;
    });
  cache.set(url, p);
  return p;
}

/** Decode the sibling's first gallery image ahead of the tap. @param {Document} doc */
function warmFirstImage(doc) {
  const img = doc.querySelector(`main ${MEDIA_SELECTOR} img`);
  if (!(img instanceof HTMLImageElement)) return;
  const i = new Image();
  if (img.sizes) i.sizes = img.sizes;
  if (img.srcset) i.srcset = img.srcset;
  i.src = img.src;
}

/** Horizon scrolls the page inside .page-wrapper, not the window (window.scrollY stays 0). */
function scrollerOf() {
  for (let n = document.querySelector('main')?.parentElement; n; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
  }
  return null;
}

/** morph node key: explicit swap key, else the element id (Horizon default). @param {Node | undefined} node */
function nodeKey(node) {
  if (!(node instanceof Element)) return undefined;
  return node.getAttribute('data-vt-swap-key') || node.id || undefined;
}

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {Document} doc
 * @param {string} url
 * @param {{ anchor?: HTMLAnchorElement | null, push?: boolean }} opts
 */
function apply(doc, url, { anchor = null, push = true } = {}) {
  const main = document.querySelector('main');
  const nextMain = doc.querySelector('main');
  if (!main || !nextMain) {
    location.assign(url);
    return;
  }
  const scroller = scrollerOf();
  const anchorTop = anchor ? anchor.getBoundingClientRect().top : null;

  const run = () => {
    morph(main, nextMain, { ...MORPH_OPTIONS, getNodeKey: nodeKey });
    document.title = doc.title || document.title;
    const canon = document.querySelector('link[rel="canonical"]');
    const nextCanon = doc.querySelector('link[rel="canonical"]');
    if (canon && nextCanon) canon.setAttribute('href', nextCanon.getAttribute('href') || '');

    // Keep the tapped control under the finger: the gallery above may change height.
    if (anchorTop !== null) {
      const path = url.split('?')[0];
      const row = anchor.classList.contains('pdp-ladder__rung') ? 'pdp-ladder__rung' : 'pdp-dots__opt';
      const next = main.querySelector(`a.${row}[aria-current]`) || main.querySelector(`a.${row}[href="${path}"]`);
      if (next) {
        const delta = next.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 1) {
          if (scroller) scroller.scrollTop += delta;
          else window.scrollBy(0, delta);
        }
      }
    }
  };

  const finish = () => {
    if (push) {
      if (!history.state?.vtSwap) history.replaceState({ vtSwap: true, url: location.pathname + location.search }, '');
      history.pushState({ vtSwap: true, url }, '', url);
    }
    document.dispatchEvent(new CustomEvent('vellatini:pdp:swapped', { detail: { url } }));
    // Shopify standard product_viewed for the sibling (createViewEventElement API).
    const pc = /** @type {any} */ (main.querySelector('product-component'));
    if (typeof pc?.dispatchViewEvent === 'function') setTimeout(() => pc.dispatchViewEvent(), 0);
  };

  const media = main.querySelector(MEDIA_SELECTOR);
  if (typeof document.startViewTransition === 'function' && !reducedMotion() && media instanceof HTMLElement) {
    media.style.viewTransitionName = 'vt-pdp-media';
    const vt = document.startViewTransition(run);
    vt.updateCallbackDone.then(finish, finish);
    const clear = () => {
      const m = main.querySelector(MEDIA_SELECTOR);
      if (m instanceof HTMLElement) m.style.viewTransitionName = '';
    };
    vt.finished.then(clear, clear);
  } else {
    run();
    finish();
  }
}

/** @param {HTMLAnchorElement} a */
function go(a) {
  if (swapping) return;
  swapping = true;
  const url = urlOf(a);
  const main = document.querySelector('main');
  a.setAttribute('aria-busy', 'true');
  const slow = setTimeout(() => main?.setAttribute('data-vt-swapping', ''), 120);
  fetchDoc(url)
    .then((doc) => {
      clearTimeout(slow);
      main?.removeAttribute('data-vt-swapping');
      a.removeAttribute('aria-busy');
      if (!doc) {
        location.assign(url);
        return;
      }
      apply(doc, url, { anchor: a });
    })
    .finally(() => {
      swapping = false;
    });
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (window.Shopify?.designMode) return;
  const a = /** @type {Element} */ (event.target).closest(SELECTOR);
  if (!isSiblingLink(a)) return;
  event.preventDefault();
  go(/** @type {HTMLAnchorElement} */ (a));
});

// Intent prefetch: hover / touch / focus on a dot fetches the sibling and decodes its hero.
const prefetch = (event) => {
  const a = /** @type {Element} */ (event.target).closest?.(SELECTOR);
  if (!isSiblingLink(a)) return;
  fetchDoc(urlOf(/** @type {HTMLAnchorElement} */ (a))).then((doc) => doc && warmFirstImage(doc));
};
document.addEventListener('pointerover', prefetch, { passive: true });
document.addEventListener('touchstart', prefetch, { passive: true });
document.addEventListener('focusin', prefetch);

// Back / forward between swapped siblings re-renders from cache (or network), never a stale page.
window.addEventListener('popstate', (event) => {
  if (!event.state?.vtSwap) return;
  const url = location.pathname + location.search;
  fetchDoc(url).then((doc) => (doc ? apply(doc, url, { push: false }) : location.reload()));
});
